import React, { useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material'
import DataTable from 'react-data-table-component'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WebhookIcon from '@mui/icons-material/Webhook'

const GET_FD_WEBHOOK_LOGS = gql`
  query GetFDWebhookLogs($limit: Int, $offset: Int) {
    getFDWebhookLogs(limit: $limit, offset: $offset) {
      total
      logs {
        _id
        fdOrderId
        rmfOrderId
        statusV3
        rmfStatus
        driverName
        driverPhone
        trackerLink
        rawPayload
        error
        createdAt
      }
    }
  }
`

const statusColors = {
  runner_assigned:  { bg: '#dbeafe', color: '#1e40af', label: 'Runner Assigned' },
  en_route_pickup:  { bg: '#fef3c7', color: '#92400e', label: 'En Route Pickup' },
  arrived_pickup:   { bg: '#fef9c3', color: '#713f12', label: 'Arrived Pickup' },
  picked_up:        { bg: '#e0f2fe', color: '#0369a1', label: 'Picked Up' },
  en_route_dropoff: { bg: '#ede9fe', color: '#5b21b6', label: 'En Route Dropoff' },
  arrived_dropoff:  { bg: '#f0fdf4', color: '#166534', label: 'Arrived Dropoff' },
  completed:        { bg: '#dcfce7', color: '#14532d', label: 'Completed' },
  cancelled:        { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
}

const PAGE_SIZE = 50

export default function FDWebhookLogs() {
  const [search, setSearch] = useState('')
  const [rawDialog, setRawDialog] = useState(null)
  const [page, setPage] = useState(1)

  const { data, loading, refetch } = useQuery(GET_FD_WEBHOOK_LOGS, {
    variables: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    fetchPolicy: 'network-only',
  })

  const logs = data?.getFDWebhookLogs?.logs ?? []
  const total = data?.getFDWebhookLogs?.total ?? 0

  const filtered = search
    ? logs.filter(l =>
        [l.rmfOrderId, l.fdOrderId, l.statusV3, l.driverName, l.driverPhone]
          .some(v => v && v.toLowerCase().includes(search.toLowerCase()))
      )
    : logs

  const columns = [
    {
      name: 'Time',
      selector: r => r.createdAt,
      cell: r => (
        <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
          {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
        </Typography>
      ),
      width: '160px',
    },
    {
      name: 'RMF Order',
      selector: r => r.rmfOrderId,
      cell: r => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {r.rmfOrderId || '—'}
        </Typography>
      ),
      width: '120px',
    },
    {
      name: 'FD Status', hide: 'md',
      cell: r => {
        const cfg = statusColors[r.statusV3] || { bg: '#f1f5f9', color: '#475569', label: r.statusV3 }
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 11, border: `1px solid ${cfg.color}22` }}
          />
        )
      },
      width: '160px',
    },
    {
      name: 'RMF Status', hide: 'md',
      cell: r => (
        <Chip
          label={r.rmfStatus || '—'}
          size="small"
          sx={{ bgcolor: r.error ? '#fee2e2' : '#f0fdf4', color: r.error ? '#991b1b' : '#166534', fontWeight: 600, fontSize: 11 }}
        />
      ),
      width: '140px',
    },
    {
      name: 'Driver', hide: 'sm',
      cell: r => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.driverName || '—'}</Typography>
          {r.driverPhone && <Typography variant="caption" sx={{ color: '#64748b' }}>{r.driverPhone}</Typography>}
        </Box>
      ),
      width: '160px',
    },
    {
      name: 'Tracker', hide: 'sm',
      cell: r => r.trackerLink
        ? <a href={r.trackerLink} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 12 }}>View</a>
        : <Typography variant="caption" sx={{ color: '#94a3b8' }}>—</Typography>,
      width: '80px',
    },
    {
      name: 'Error', hide: 'sm',
      cell: r => r.error
        ? <Typography variant="caption" sx={{ color: '#dc2626', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.error}</Typography>
        : <Typography variant="caption" sx={{ color: '#22c55e' }}>OK</Typography>,
      grow: 1,
    },
    {
      name: 'Raw', hide: 'sm',
      cell: r => (
        <IconButton size="small" onClick={() => setRawDialog(r)} sx={{ color: '#6366f1' }}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      ),
      width: '60px',
      center: true,
    },
  ]

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WebhookIcon sx={{ color: '#6366f1', fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                First Delivery Webhook Logs
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Monitor delivery status updates from First Delivery DSP
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => refetch()} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
          {[
            { label: 'Total Webhooks', value: total, color: '#6366f1' },
            { label: 'Errors', value: logs.filter(l => l.error).length, color: '#ef4444' },
            { label: 'Successful', value: logs.filter(l => !l.error).length, color: '#22c55e' },
          ].map(s => (
            <Paper key={s.label} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: s.color }}>{s.value}</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>{s.label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
            <TextField
              placeholder="Search by order ID, status, driver..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ width: 340 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }}
            />
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={filtered}
              pagination
              paginationServer
              paginationTotalRows={total}
              paginationPerPage={PAGE_SIZE}
              onChangePage={p => setPage(p)}
              paginationComponentOptions={{ noRowsPerPage: true }}
              customStyles={{
                headCells: { style: { backgroundColor: '#f8fafc', fontWeight: 700, fontSize: 12, color: '#475569', textTransform: 'uppercase' } },
                rows: { style: { '&:hover': { backgroundColor: '#f8fafc' } } },
              }}
              noDataComponent={
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography sx={{ color: '#94a3b8' }}>No webhook logs yet. Register the webhook URL in First Delivery and place a test order.</Typography>
                </Box>
              }
            />
          )}
        </Paper>
      </Container>

      {/* Raw Payload Dialog */}
      <Dialog open={!!rawDialog} onClose={() => setRawDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Raw Webhook Payload — Order {rawDialog?.rmfOrderId}
        </DialogTitle>
        <DialogContent>
          {rawDialog?.error && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fee2e2', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 600 }}>Error: {rawDialog.error}</Typography>
            </Box>
          )}
          <Box component="pre" sx={{ bgcolor: '#1e293b', color: '#e2e8f0', p: 2, borderRadius: 2, fontSize: 12, overflow: 'auto', maxHeight: 400 }}>
            {rawDialog?.rawPayload
              ? JSON.stringify(JSON.parse(rawDialog.rawPayload), null, 2)
              : '—'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
